import React, { useEffect, useCallback, useRef } from 'react';
import './ConnectionTest.css';
import API_BASE from '../../apiConfig';

const BACKEND_URL = API_BASE;
const RETRY_DELAY = 5000; // 5 seconds between retries

const ConnectionTest = ({ connectionStatus, setConnectionStatus, setError, compact = false }) => {
  const retryTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  const testConnection = useCallback(async (isRetry = false) => {
    if (!isRetry && connectionStatus === 'connected') {
      return; // Skip if we're already connected and this isn't a retry
    }

    try {
      setConnectionStatus('connecting');
      const response = await fetch(`${BACKEND_URL}/health`);

      if (!isMounted.current) return; // Skip if component unmounted

      if (response.ok) {
        const data = await response.json();
        setConnectionStatus('connected');
        setError(null);
        console.log('✅ Backend connection successful:', data);
        return data;
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (err) {
      if (!isMounted.current) return; // Skip if component unmounted

      console.error('❌ Backend connection failed:', err);
      setConnectionStatus('disconnected');
      setError(`Connection failed: ${err.message}`);

      // Schedule a retry
      if (isMounted.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(() => {
          testConnection(true);
        }, RETRY_DELAY);
      }

      return null;
    }
  }, [connectionStatus, setConnectionStatus, setError]);

  // Initial connection test on mount and when testConnection changes
  useEffect(() => {
    isMounted.current = true;
    testConnection();

    // Cleanup function
    return () => {
      isMounted.current = false;
      clearTimeout(retryTimeoutRef.current);
    };
  }, [testConnection]); // Include testConnection in dependencies

  if (compact) {
    return (
      <div className="connection-test compact">
        <div className={`status-indicator status-${connectionStatus}`}>
          {connectionStatus === 'connected' ? (
            <>
              <svg className="status-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              <span className="status-text">Connected</span>
            </>
          ) : connectionStatus === 'connecting' ? (
            <>
              <svg className="status-icon spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
              <span className="status-text">Connecting...</span>
            </>
          ) : (
            <>
              <svg className="status-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
              <span className="status-text">Disconnected</span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="connection-test card">
      <div className="connection-header">
        <div className="connection-info">
          <h3>🔌 Backend Connection</h3>
          <div className={`status-indicator status-${connectionStatus}`}>
            {connectionStatus === 'connected' ? (
              <>
                <svg className="status-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                <span className="status-text">Connected</span>
              </>
            ) : connectionStatus === 'connecting' ? (
              <>
                <svg className="status-icon spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
                <span className="status-text">Testing...</span>
              </>
            ) : (
              <>
                <svg className="status-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                <span className="status-text">Disconnected</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => testConnection()}
          className="btn btn-primary"
          disabled={connectionStatus === 'connecting'}
        >
          🔄 Test Connection
        </button>
      </div>
    </div>
  );
};

export default ConnectionTest;
